import { useEffect, useRef, useState } from "react";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import useToken from "../../../../hooks/useToken";
import { useGlobalStyles } from "../../../../components/globalStyles";
import { landingStrings } from "../../../../components/StringBundle";
import brandLogo from "../../../../assets/images/Home/continental-coffee-logo.jpg";
import coffeePlant from "../../../../assets/images/Home/coffee-plant-illustration.webp";
import coffeeBeans from "../../../../assets/images/Home/coffee-beans-illustration.webp";
import type { TypewriterHandle } from "../../../admin/types";
import { createTypewriter } from "./landingConstant";

const MESSAGES_BY_PERIOD = landingStrings.messagesByPeriod;
const WELCOME_TITLES = landingStrings.welcomeTitles;
const TIME_PERIODS = landingStrings.timePeriods;

/* How often the title (and, if the 4-hour slot has rolled over, the message pool) re-rolls */
const ROTATION_INTERVAL_MS = 5 * 60 * 1000;

/* For the first few minutes of each new 4-hour slot, show that slot's own title/message first */
const SLOT_INTRO_WINDOW_MS = 15 * 60 * 1000;

function getTimePeriod(hour: number): string {
  if (hour < 4) return TIME_PERIODS.midnightGrind;
  if (hour < 8) return TIME_PERIODS.riseAndBrew;
  if (hour < 12) return TIME_PERIODS.happyMorning;
  if (hour < 16) return TIME_PERIODS.middayMomentum;
  if (hour < 20) return TIME_PERIODS.eveningWrapUp;
  return TIME_PERIODS.windingDown;
}

function isInSlotIntroWindow(): boolean {
  const now = new Date();
  const slotStart = new Date(now);
  slotStart.setHours(Math.floor(now.getHours() / 4) * 4, 0, 0, 0);
  return now.getTime() - slotStart.getTime() < SLOT_INTRO_WINDOW_MS;
}

// Tracks the last index picked for each key in memory, so this render session doesn't redraw the same pick twice in a row.
const previousIndexByKey: Record<string, number> = {};

function pickFreshIndex(key: string, length: number): number {
  if (length <= 1) return 0;
  const previous = previousIndexByKey[key] ?? -1;
  let index: number;
  do {
    index = Math.floor(Math.random() * length);
  } while (index === previous);
  previousIndexByKey[key] = index;
  return index;
}

const Landing = () => {
  const classes = useGlobalStyles() as Record<string, string>;
  const { getCurrentUserDetails } = useToken();
  const { name, firstName, lastName } = getCurrentUserDetails();
  const fullName = [firstName, lastName].filter(Boolean).join(" ");
  const userName: string = fullName || name || landingStrings.defaultUserName;

  const [title, setTitle] = useState<string>("");
  const [typedName, setTypedName] = useState<string>("");
  const [nameCursorVisible, setNameCursorVisible] = useState<boolean>(false);
  const [typedSub, setTypedSub] = useState<string>("");
  const [subCursorVisible, setSubCursorVisible] = useState<boolean>(false);

  const periodRef = useRef<string>(getTimePeriod(new Date().getHours()));
  const nameLoopRef = useRef<TypewriterHandle | null>(null);
  const subLoopRef = useRef<TypewriterHandle | null>(null);

  useEffect(() => {
    const applyTitle = () => {
      const nextTitle = isInSlotIntroWindow()
        ? periodRef.current
        : WELCOME_TITLES[
            pickFreshIndex("ccl-welcome-title-index", WELCOME_TITLES.length)
          ];
      setTitle(nextTitle);
    };

    const startMessageLoop = () => {
      if (subLoopRef.current) subLoopRef.current.stop();
      const messages = MESSAGES_BY_PERIOD[periodRef.current as keyof typeof MESSAGES_BY_PERIOD];
      const startIndex = isInSlotIntroWindow()
        ? 0
        : pickFreshIndex(
            `ccl-welcome-message-index:${periodRef.current}`,
            messages.length,
          );
      subLoopRef.current = createTypewriter({
        texts: messages,
        typingSpeed: 40,
        deletingSpeed: 24,
        pauseDuration: 2000,
        loop: true,
        startIndex,
        onChar: setTypedSub,
      });
    };

    applyTitle();

    const nameTimer = setTimeout(() => {
      setNameCursorVisible(true);
      nameLoopRef.current = createTypewriter({
        texts: [userName],
        typingSpeed: 160,
        loop: false,
        onChar: setTypedName,
        onDone: () => {
          setNameCursorVisible(false);
          setSubCursorVisible(true);
          startMessageLoop();
        },
      });
    }, 550);

    const rotationTimer = setInterval(() => {
      applyTitle();
      const nextPeriod = getTimePeriod(new Date().getHours());
      if (nextPeriod !== periodRef.current) {
        periodRef.current = nextPeriod;
        startMessageLoop();
      }
    }, ROTATION_INTERVAL_MS);

    return () => {
      clearTimeout(nameTimer);
      clearInterval(rotationTimer);
      if (nameLoopRef.current) nameLoopRef.current.stop();
      if (subLoopRef.current) subLoopRef.current.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box component="main" className={classes.welcomeRoot}>
      <Box className={classes.welcomeField} aria-hidden="true">
        <img
          className={`${classes.welcomeIllustration} ${classes.welcomeIllustrationPlant}`}
          src={coffeePlant}
          alt=""
        />
        <img
          className={`${classes.welcomeIllustration} ${classes.welcomeIllustrationBeans}`}
          src={coffeeBeans}
          alt=""
        />
      </Box>
      <Box className={classes.welcomeGrain} aria-hidden="true" />

      <Box component="section" className={classes.welcomeStage}>
        <Box component="header" className={classes.welcomeBrandRow}>
          <Box className={classes.welcomeLogo}>
            <img
              src={brandLogo}
              alt="Continental Coffee logo"
            />
          </Box>
          <Box>
            <Typography
              component="p"
              variant="inherit"
              className={classes.welcomeBrand}
            >
              {landingStrings.brandName}
            </Typography>
            <Typography
              component="p"
              variant="inherit"
              className={classes.welcomeBrandSub}
            >
              {landingStrings.brandTagline}
            </Typography>
          </Box>
        </Box>

        <Typography
          component="h1"
          variant="inherit"
          className={classes.welcomeGreeting}
        >
          {title ? `${title}, ` : ""}
          <span className={classes.welcomeNameSlot}>
            <span className={classes.welcomeNameGhost} aria-hidden="true">
              {userName}
            </span>
            <span className={classes.welcomeNameLive}>
              <span className={classes.welcomeGreetingName}>{typedName}</span>
              {nameCursorVisible && (
                <span className={classes.welcomeCursor}>|</span>
              )}
            </span>
          </span>
        </Typography>
      </Box>
    </Box>
  );
};

export default Landing;
